import { useEffect, useRef, useState } from "react"
import { Loader2 } from "lucide-react"

import AuthAlert from "@/components/tuw/auth/AuthAlert"
import { PasswordField, TextField } from "@/components/tuw/auth/fields"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { recaptchaAnswer, RecaptchaWidget, resetRecaptcha } from "@/pages/Auth/SignUp"
import { signInToContinue } from "@/lib/openHouse/guest"
import { GUEST, REGISTRATION as R, WORDING } from "@/lib/openHouse/wording"
import { cn } from "@/lib/utils"
import { ErrorLine } from "./UploadControl"

// Reservation's account screen (spec P3-H6; BRIEF-2 step 5; guest-wording §3):
// the guest creates their account to post the draft. Fields are checked here
// by the server's own rules (OpenHouse::AccountsController#field_problems), and
// the server's `invalid` fields show the same messages. onPost(fields) resolves
// once the page has moved on (posted, or a refusal it routes elsewhere) and
// rejects with the refusals this screen shows. The password lives only in this
// screen's state and the request body.
//
// `account` is the server's { recaptcha, policies, passwordLength, forgotPassword }.
// `askReminder`: no reminder consent is held yet, so the screen offers
// Bookmark's one-reminder checkbox (guest-wording §1), its own box, unchecked,
// apart from the registration agreement; ticked, onPost carries reminderConsent.
//
// `words` replaces the screen's own lines (Viewing Room's account box,
// lib/viewingRoom/enrollment.js accountWords); a null line is left out.
// `onSignIn` is where Sign in goes, the draft's continuation by default.

const EMAIL = /^[^@\s]+@[^@\s]+$/ // Devise.email_regexp
const NAME_LIMIT = 255
const EMAIL_LIMIT = 254
const ORDER = ["first_name", "last_name", "email", "password", "agreement"]
const ID = { first_name: "oh-first-name", last_name: "oh-last-name", email: "oh-email", password: "oh-password", agreement: "oh-agreement" }
const LINK = "font-semibold text-foreground underline underline-offset-2"

const chars = (text) => [...text].length

function problems({ first, last, email, password, agreed }, { min, max }) {
  const name = (value) => (!value.trim() ? "required" : chars(value.trim()) > NAME_LIMIT ? "too_long" : null)
  const address = email.trim()
  const length = chars(password)
  const found = {
    first_name: name(first),
    last_name: name(last),
    email: !address ? "required" : chars(address) > EMAIL_LIMIT || !EMAIL.test(address) ? "invalid" : null,
    password: !length ? "required" : length < min ? "too_short" : length > max ? "too_long" : null,
    agreement: agreed ? null : "required",
  }
  return Object.fromEntries(Object.entries(found).filter(([, problem]) => problem))
}

function message(field, problem, { min, max }, words) {
  const text = {
    first_name: { required: R.firstNameRequired, too_long: R.nameTooLong(NAME_LIMIT) },
    last_name: { required: R.lastNameRequired, too_long: R.nameTooLong(NAME_LIMIT) },
    email: { required: R.emailRequired, invalid: R.emailInvalid },
    password: { required: R.passwordRequired, too_short: R.passwordTooShort(min), too_long: R.passwordTooLong(max) },
    agreement: { required: words.agreementRequired ?? R.agreementRequired },
  }[field]?.[problem]
  return text || R.pending(`${field}.${problem}`)
}

// The screen's own lines, which `words` may replace.
const WORDS = {
  heading: R.heading, body: R.body, nameHelp: R.nameHelp, submit: R.submit, submitting: R.submitting,
  confirmationRequired: R.confirmationRequired, billingLater: R.billingLater, validationSummary: R.validationSummary,
  agreementRequired: R.agreementRequired, existingBody: R.existingBody, failedBody: R.failedBody,
}

export function AccountScreen({ account, askReminder = false, onPost, words: lines = {}, onSignIn = signInToContinue }) {
  const words = { ...WORDS, ...lines }
  const length = account.passwordLength
  const [form, setForm] = useState({ first: "", last: "", email: "", password: "", agreed: false, reminder: false })
  const [showErr, setShowErr] = useState(false)
  const [serverFields, setServerFields] = useState({})
  const [alert, setAlert] = useState(null) // { text } | { heading, text, retry }
  const [exists, setExists] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [focusKey, setFocusKey] = useState(null)
  const formRef = useRef(null)

  useEffect(() => {
    if (!focusKey) return
    document.getElementById(ID[focusKey])?.focus()
    setFocusKey(null)
  }, [focusKey])

  const client = problems(form, length)
  const errors = { ...(showErr ? client : {}), ...serverFields }
  const errorOf = (field) => errors[field] && message(field, errors[field], length, words)
  const hasErrors = Object.keys(errors).length > 0

  const set = (key, field) => (value) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setServerFields((prev) => Object.fromEntries(Object.entries(prev).filter(([key]) => key !== field)))
  }

  // A spent or refused answer needs a fresh security check.
  const refused = (err) => {
    if (account.recaptcha) resetRecaptcha(formRef.current)
    const { reason, data = {} } = err || {}
    if (reason === "invalid") {
      setServerFields(data.fields || {})
      setFocusKey(ORDER.find((field) => data.fields?.[field]))
    } else if (reason === "account_exists") {
      setExists(true)
    } else if (reason === "security_check") {
      setAlert({ text: R.securityCheck })
    } else if (reason === "too_many_attempts") {
      setAlert({ text: R.tooManyAttempts })
    } else if (reason === "price_mismatch") {
      // The draft was found open and unchanged: the answers are saved.
      setAlert({ text: WORDING.postFail, retry: WORDING.postRetry })
    } else {
      setAlert({ heading: R.failedHeading, text: words.failedBody, retry: R.failedRetry })
    }
  }

  const submit = async (event) => {
    event?.preventDefault()
    if (submitting) return
    setShowErr(true)
    setServerFields({})
    setAlert(null)
    const first = ORDER.find((field) => client[field])
    if (first) return setFocusKey(first)
    const token = recaptchaAnswer(formRef.current)
    if (account.recaptcha && !token) return setAlert({ text: R.securityCheck })

    setSubmitting(true)
    try {
      await onPost({
        firstName: form.first.trim(), lastName: form.last.trim(), email: form.email.trim(), password: form.password,
        reminderConsent: askReminder && form.reminder, recaptchaToken: token,
      })
    } catch (err) {
      refused(err)
      setSubmitting(false)
    }
  }

  const useAnother = () => {
    setExists(false)
    setShowErr(false)
    setForm((prev) => ({ ...prev, email: "" }))
    setFocusKey("email")
  }

  const [lead, signIn] = R.signIn.split(/(?<=\?) /)
  const policies = account.policies || {}

  return (
    <div className="mx-auto flex max-w-[560px] flex-col gap-5 px-8 pt-8 pb-14 max-[561px]:px-4">
      {exists && (
        <section aria-labelledby="oh-exists" className="flex flex-col items-start gap-3">
          <h1 id="oh-exists" className="font-display text-[22px] font-bold tracking-tight text-foreground">{R.existingHeading}</h1>
          <p className="text-[13.5px] leading-relaxed text-muted-foreground">{words.existingBody}</p>
          <div className="flex w-full flex-wrap gap-2 max-[561px]:flex-col">
            <Button onClick={() => onSignIn()}>{R.existingButton}</Button>
            <Button variant="outline" onClick={useAnother}>{R.existingUseAnother}</Button>
          </div>
          <a href={account.forgotPassword} className={cn(LINK, "text-[13px]")}>{R.existingForgot}</a>
        </section>
      )}

      {/* Hidden, not unmounted, behind the existing-account panel: the security check keeps its widget. */}
      <form ref={formRef} noValidate onSubmit={submit} className={cn("flex flex-col gap-4", exists && "hidden")}>
        <div className="flex flex-col gap-1.5">
          <h1 className="font-display text-[22px] font-bold tracking-tight text-foreground">{words.heading}</h1>
          <p className="text-[13.5px] leading-relaxed text-muted-foreground">{words.body}</p>
        </div>

        {hasErrors && <AuthAlert tone="bad">{words.validationSummary}</AuthAlert>}
        {alert && (
          <AuthAlert tone="bad">
            {alert.heading && <b className="block">{alert.heading}</b>}
            {alert.text}
            {alert.retry && (
              <Button type="button" variant="outline" size="sm" className="mt-2 flex" onClick={submit} disabled={submitting}>
                {alert.retry}
              </Button>
            )}
          </AuthAlert>
        )}

        <div className="flex flex-col gap-[7px]">
          <div className="grid gap-4 min-[561px]:grid-cols-2">
            <TextField id={ID.first_name} label={R.firstNameLabel} autoComplete="given-name" value={form.first}
              onChange={(e) => set("first", "first_name")(e.target.value)} error={errorOf("first_name")} />
            <TextField id={ID.last_name} label={R.lastNameLabel} autoComplete="family-name" value={form.last}
              onChange={(e) => set("last", "last_name")(e.target.value)} error={errorOf("last_name")} />
          </div>
          {words.nameHelp && <p className="text-[12px] leading-[1.45] text-ink-3">{words.nameHelp}</p>}
        </div>
        <TextField id={ID.email} type="email" label={R.emailLabel} autoComplete="email" value={form.email} hint={R.emailHelp}
          onChange={(e) => set("email", "email")(e.target.value)} error={errorOf("email")} />
        <PasswordField id={ID.password} label={R.passwordLabel} autoComplete="new-password" value={form.password}
          hint={R.passwordHelp(length.min, length.max)} onChange={(e) => set("password", "password")(e.target.value)}
          error={errorOf("password")} />

        <div className="flex flex-col gap-2">
          <label className={cn("flex cursor-pointer items-start gap-3 rounded-xl border px-4 py-3.5",
            errorOf("agreement") ? "border-destructive" : "border-line-2")}>
            <Checkbox id={ID.agreement} checked={form.agreed} onCheckedChange={(v) => set("agreed", "agreement")(v === true)}
              aria-invalid={!!errorOf("agreement")} aria-describedby="oh-agreement-help" className="mt-0.5" />
            <span className="flex flex-col gap-1 text-[12.5px] leading-relaxed text-foreground">
              <span>
                {R.agreement.map((part, i) => {
                  if (typeof part === "string") return <span key={i}>{part}</span>
                  const [key, label] = part
                  if (!policies[key]) return <span key={i}>{label}</span>
                  return <a key={i} href={policies[key]} target="_blank" rel="noreferrer" className={LINK}>{label}</a>
                })}
              </span>
              <span id="oh-agreement-help" className="text-muted-foreground">{R.agreementHelp}</span>
            </span>
          </label>
          <ErrorLine>{errorOf("agreement")}</ErrorLine>
        </div>

        {askReminder && (
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-line-2 px-4 py-3.5">
            <Checkbox id="oh-reminder" checked={form.reminder} onCheckedChange={(v) => set("reminder")(v === true)}
              aria-describedby="oh-reminder-help" className="mt-0.5" />
            <span className="flex flex-col gap-1 text-[12.5px] leading-relaxed text-foreground">
              {GUEST.reminderConsent}
              <span id="oh-reminder-help" className="text-muted-foreground">{GUEST.reminderConsentHelp}</span>
            </span>
          </label>
        )}

        <RecaptchaWidget recaptcha={account.recaptcha} />

        <Button type="submit" disabled={submitting} className="gap-1.5">
          {submitting && <Loader2 className="size-4 animate-spin" />}
          {submitting ? words.submitting : words.submit}
        </Button>

        <div className="flex flex-col gap-1.5 text-[12.5px] leading-relaxed text-muted-foreground">
          <p>{words.confirmationRequired}</p>
          {words.billingLater && <p>{words.billingLater}</p>}
          <p>
            {lead}{" "}
            <button type="button" onClick={() => onSignIn()} className={LINK}>{signIn}</button>
          </p>
        </div>
      </form>
    </div>
  )
}
